import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import cmsPreview from '@/mixins/cmsPreview';

export default {
  mixins: [cmsPreview],
  data() {
    return {
      adjustedMaterialWidth: 0,
      leftWidth: null,
      rightWidth: null,
      video: null,
      chat: null,
      mainLayout: null,
      switchingPercentage: 0.64,
      maximumMaterialWidth: 0.75471,
      initialPdfWidth: 0,
      isPdfWidthAdjusted: false, // pdf width pinned by a manual slider drag
      menuChatHeight: false,
      adjustedLayout: {
        '1': {},
        '2': {},
        '3': {}
      }
    };
  },
  mounted() {
    this.leftWidth = document.querySelector('.left_contents');
    this.rightWidth = document.querySelector('.right_contents');
    this.video = document.querySelector('.video');
    this.chat = document.querySelector('.chatbox');
    this.mainLayout = document.querySelector('#main-layout');
  },
  watch: {
    isFetchingMaterial(val) {
      // a newly selected material always starts fitted to the panel
      if (val) {
        this.fitPDFToPanel();
      }
      if (this.isMaterialLayout && !val) {
        this.setCurrrentLayoutStyle();
        this.setChatHeight();
      }
    },
    pdfZoom() {
      this.retainPDFWidth();
    },
    // re-selecting a layout (even the current one) fits the material to the panel again
    layoutSelectCount() {
      this.isPdfWidthAdjusted = false;
      // after the layoutStyle watchers' $nextTick so the panel already has its new width
      this.$nextTick(() => this.fitPDFToPanel());
    },
    isTrainerLayout1View(val) {
      if (val) this.retainPDFWidth();
    },
    showVocabularyBook(show) {
      const showWidth = show ? 200 : -200;
      this.leftWidth.style.width = this.getPercentageWidth(this.leftWidth.offsetWidth + showWidth) + '%';
      this.rightWidth.style.width = 100 - parseFloat(this.leftWidth.style.width) + '%';
      this.adjustedMaterialWidth = this.leftWidth.offsetWidth;
      if (!show) {
        this.switchBetweenLayout1And2(false);
      }
      this.setChatHeight();
    },
    isMenuVisible() {
      if (this.$isMobileSize()) {
        return;
      }
      // customized chat height computation due to drawer animation time
      this.menuChatHeight = true;
      this.chat.style.transition = 'height 0.30s ease, width 0.30s ease';
      this.resetAdjustedSize();
      this.setChatHeight();
      this.retainPDFWidth();
      this.adjustPhraseDialogPosition();
      setTimeout(() => {
        this.menuChatHeight = false;
        this.chat.style.transition = 'unset';
      }, 150);
    },
    // re-fit the video/chat column when the visible viewport height changes
    // (e.g. Chrome address bar show/hide, orientation change)
    viewportHeight() {
      if (this.$refs.chatBox && this.$refs.videoChat && this.video) {
        this.setChatAndVideoSize();
      }
    }
  },
  computed: {
    ...mapState('layout', ['layoutStyle', 'sliderLayout', 'isMenuVisible', 'viewportHeight', 'layoutSelectCount']),
    ...mapState('material', ['pdfZoom', 'showVocabularyBook', 'showPDFHtml', 'isFetchingMaterial']),
    ...mapState('htmlMaterial', ['htmlIsRevamp']),
    ...mapGetters('layout', [
      'isDefaultLayout',
      'isMaterialLayout',
      'isVideoScreenLayout',
      'isTrainerLayout1View'
    ]),
    ...mapGetters(['isAssistLesson']),

    // stored layout size for layout 1 and 2
    newLayoutSize() {
      return this.adjustedLayout[this.layoutStyle];
    },

    // material width reach 64% of window innerWidth
    toLayout2() {
      return this.switchingPercentage <= this.adjustedMaterialWidth / (this.windowInnerWidth() - 48);
    },

    // check if material width is at max
    isMaterialInMaxWidth() {
      return this.adjustedMaterialWidth >= this.windowInnerWidth() * this.maximumMaterialWidth - 36;
    }
  },
  methods: {
    ...mapMutations('layout', ['setSliderPdfWidth']),
    ...mapMutations('material', ['setShowVocabularyBook']),
    ...mapActions('layout', ['switchLayoutStyle', 'switchSliderLayout']),

    // adjust the height of video and chat component
    adjustVertically(e) {
      e.preventDefault();
      this.setDefaultSize();

      let dragX = e.clientY;
      const vm = this;
      document.onmousemove = e => {
        const adjustedVideoHeight = vm.video.offsetHeight + (e.clientY - dragX);
        const isMaxVideoHeight = vm.video.offsetWidth * (9 / 16) <= adjustedVideoHeight;
        const isVideoScalingUp = e.clientY > dragX;
        const isVideoHasHeight = vm.video.offsetHeight > 0;
        const deviceSelection = e.clientY - 73;

        vm.setVideoMarginBottom(isVideoScalingUp, deviceSelection);

        const scalingWithDevice = isVideoScalingUp && deviceSelection > this.videoMarginBottom();

        if ((scalingWithDevice || isVideoHasHeight) && !isMaxVideoHeight) {
          vm.video.style.height = vm.newLayoutSize['videoHeight'] = adjustedVideoHeight + 'px';
          vm.setSmallVideoSize();
          dragX = e.clientY;
          vm.setChatAndVideoSize();
        }
      };
      // remove mouse-move listener on mouse-up
      document.onmouseup = () => (document.onmousemove = document.onmouseup = null);
    },

    // adjust the width between left and right side contents
    adjustHorizontally(e) {
      e.preventDefault();
      if (this.isVideoScreenLayout) {
        this.adjustVideoScreenLayout(e);
      } else {
        this.adjustDefaultLayout(e);
      }
    },

    // adjust chat component height when the video changes its height
    setChatHeight() {
      const videoMargin = parseInt(this.video.style.marginBottom) || this.videoMarginBottom();
      let extraHeight = !this.$isMobileSize() ? videoMargin + 97 : 128;
      let chatHeight = this.viewportHeight - this.getVideoHeight() - extraHeight + 'px';
      this.$refs.chatBox.wrapperHeight = chatHeight;
    },

    // compute the video height when the menu is getting open/close
    getVideoHeight() {
      let videoHeight = this.$refs.videoChat.$el.offsetHeight;
      if (this.menuChatHeight && this.video.style.height === 'auto') {
        videoHeight = this.isMenuVisible
          ? this.video.offsetHeight - this.video.offsetHeight * (291.84 / window.innerWidth)
          : this.video.offsetHeight + this.video.offsetHeight * (353.28 / window.innerWidth);
      }
      return videoHeight;
    },

    // adjust computation with window innerwidth when side menu is visible
    windowInnerWidth() {
      const sideMenuWidth = this.isMenuVisible ? 280 : 0;
      return window.innerWidth - sideMenuWidth;
    },

    // set chat and video size based on the current window size
    setChatAndVideoSize() {
      this.hideComponentContents();
      this.resetAdjustedSize();
      this.setSmallVideoSize();
      this.setChatHeight();
      this.retainPDFWidth();
      this.adjustPhraseDialogPosition();
    },

    // hide video/material contents when components reach a small width
    hideComponentContents() {
      this.video.offsetHeight < 110
        ? this.video.classList.add('hide_contents')
        : this.video.classList.remove('hide_contents');

      parseInt(this.leftWidth.offsetWidth) < 120
        ? this.leftWidth.classList.add('hide_contents')
        : this.leftWidth.classList.remove('hide_contents');

      const maxWidth = this.windowInnerWidth() - 124;
      this.chat.style.visibility = this.leftWidth.offsetWidth >= maxWidth ? 'hidden' : 'visible';
    },

    // set margin of video when layout adjustment reach the device selection position
    setVideoMarginBottom(isVideoScalingUp, deviceSelection) {
      const displayDeviceSelection = this.video.offsetHeight < 10;
      const hideDeviceSelection =
        this.video.offsetHeight === 0 && parseInt(this.video.style.marginBottom) > 0;

      if (hideDeviceSelection && !isVideoScalingUp) {
        // start hiding device selection
        this.video.style.marginBottom = Math.max(8, deviceSelection) + 'px';
        this.onDeviceSelectionSizeChange();
      } else if (displayDeviceSelection && isVideoScalingUp && deviceSelection > 0) {
        // start showing device selection
        this.video.style.marginBottom = Math.min(this.videoMarginBottom(), deviceSelection) + 'px';
        this.onDeviceSelectionSizeChange();
      }
    },

    // adjust other components when device selection position size change
    onDeviceSelectionSizeChange() {
      this.newLayoutSize['videoMarginBottom'] = this.video.style.marginBottom;
      this.setChatHeight();
    },

    // set small/local video size if has local & remote stream
    setSmallVideoSize() {
      const smallVideo = document.querySelector('.video-sm');
      const smallVideoSpan = document.querySelector('.video-sm span');
      if (smallVideo) {
        const videoHeight = parseInt(this.video.style.height);
        if (!isNaN(videoHeight)) {
          smallVideo.style.width = videoHeight * 0.6578 + 'px';
          smallVideoSpan.style.height = videoHeight * 0.368 + 'px';
        } else {
          smallVideo.style.width = '37%';
          smallVideoSpan.style.height = '36.8%';
        }
        const isLayout2 = this.leftWidth.offsetWidth >= this.switchingPercentage * 100 || this.toLayout2;
        const hideSmallVideo = this.video.offsetHeight < 200 && !isLayout2;
        smallVideo.style.visibility = hideSmallVideo ? 'hidden' : 'visible';
      }
    },

    // adjust the width of left (material) and right (video & chat) contents
    adjustDefaultLayout(e) {
      this.setDefaultSize();

      let dragY = e.clientX;
      const vm = this;

      document.onmousemove = e => {
        const adjustedWidth = e.clientX - dragY;
        const adjustedMaterialWidth = (vm.adjustedMaterialWidth = vm.leftWidth.offsetWidth + adjustedWidth);

        // check if material width is scaling up if less than maximum width
        // or scaling down and minimum width has not been reached
        const resizeMaterial = vm.leftWidth.offsetWidth >= 0 || e.clientX > dragY;

        // remove the video 16:9 when slider is going left
        const isSlidingToLeft = e.clientX < dragY;
        if (isSlidingToLeft) {
          vm.video.style.height = vm.newLayoutSize['videoHeight'] = vm.video.offsetHeight + 'px';
          vm.video.style.paddingTop = '0px';
        }

        if (resizeMaterial && !vm.isMaterialInMaxWidth) {
          vm.newLayoutSize['leftWidth'] = vm.getPercentageWidth(adjustedMaterialWidth) + '%';
          vm.newLayoutSize['rightWidth'] = 100 - vm.getPercentageWidth(adjustedMaterialWidth) + '%';

          vm.switchBetweenLayout1And2();

          vm.rightWidth.style.width = vm.newLayoutSize['rightWidth'];
          vm.setChatAndVideoSize();

          vm.isPdfWidthAdjusted = true;
          vm.retainPDFWidth();
          vm.leftWidth.style.width = vm.newLayoutSize['leftWidth'];
          dragY = e.clientX;
        }
        this.$nextTick(() => this.setTrainerTagDialogPosition());
      };

      // remove mouse-move listener on mouse-up
      document.onmouseup = () => (document.onmousemove = document.onmouseup = null);
    },

    // adjust video and chat horizontally on layout 3
    adjustVideoScreenLayout(e) {
      let dragY = e.clientX;
      const leftClass = this.isAssistLesson ? '.left_side' : '.left_contents';
      const vm = this;

      document.onmousemove = e => {
        const adjustedWidth = e.clientX - dragY;
        const adjustedLeftWidth = vm.leftWidth.offsetWidth + adjustedWidth;

        const isInMinLeftWidth = vm.leftWidth.offsetWidth > 12 && e.clientX < dragY;

        if (isInMinLeftWidth || e.clientX > dragY) {
          vm.newLayoutSize['leftWidth'] = vm.getPercentageWidth(adjustedLeftWidth) + '%';
          vm.newLayoutSize['rightWidth'] = window.innerWidth - 60 - adjustedLeftWidth + 'px';

          // remove the video 16:9 when slider is going right and max height has been reached
          const isSlidingToRight = e.clientX > dragY && vm.getPercentageWidth(adjustedLeftWidth) > 75.48;
          if (isSlidingToRight) {
            vm.video.style.height = vm.newLayoutSize['videoHeight'] = window.innerWidth * 0.407 + 'px';
          }

          vm.leftWidth.style.width = document.querySelector(leftClass).style.width =
            vm.newLayoutSize['leftWidth'];
          vm.setChatAndVideoSize();
          dragY = e.clientX;
        }
        this.$nextTick(() => this.setTrainerTagDialogPosition());
      };

      // remove mouse-move listener on mouse-up
      document.onmouseup = () => (document.onmousemove = document.onmouseup = null);
    },

    // adjust phrase dialog position
    adjustPhraseDialogPosition() {
      const dialogWrapper = document.querySelector('.phrase-content .el-dialog__wrapper');
      if (!dialogWrapper) return;

      if (this.isVideoScreenLayout) {
        dialogWrapper.style.width = this.chat.style.width;
      } else {
        dialogWrapper.style.width = 'unset';
      }
      // override tab border to fix incorrect positioning
      const usefulPhraseTab = document.querySelector('.el-tabs__item.is-active');
      const tabBorderBottom = document.querySelector('.phrase-content .el-tabs__active-bar');
      if (tabBorderBottom && usefulPhraseTab && usefulPhraseTab.offsetWidth) {
        tabBorderBottom.style.width = usefulPhraseTab.offsetWidth + "px";
        tabBorderBottom.style.transform = `translateX(${usefulPhraseTab.offsetLeft}px)`;
      }
    },

    // set min and max size to prevent overresizing from the slider
    setDefaultSize() {
      this.leftWidth.style.width = this.getPercentageWidth(this.leftWidth.offsetWidth);
      this.leftWidth.style.minWidth = '0%';
      this.leftWidth.style.maxWidth = this.maximumMaterialWidth * 100 + '%';
      this.rightWidth.style.maxWidth = '99%';
      this.rightWidth.style.minWidth = '24.529%';

      this.video.style.height = this.video.offsetHeight + 'px';
      this.video.style.marginBottom = this.video.style.marginBottom || this.videoMarginBottom() + 'px';
      this.video.style.paddingTop = '0px';
      this.video.style.maxHeight = this.viewportHeight - 220 + 'px';
    },

    // switch between layout 1 and layout 2 when switching percentage is reach
    switchBetweenLayout1And2(switchLayout = false) {
      const switchToLayout2 = switchLayout || this.toLayout2;
      const layout = [2, 1];
      const [active, inactive] = switchToLayout2 ? layout : layout.sort();

      this.switchSliderLayout(active);
      this.mainLayout.classList.add('content_' + active);
      this.mainLayout.classList.remove('content_' + inactive);
      this.rightWidth.style.marginLeft = switchToLayout2 ? this.newLayoutSize['leftWidth'] : '-2px';
      if (this.video.offsetHeight > 10) {
        this.video.style.marginBottom = (switchToLayout2 ? 26 : 66) + 'px';
      }
    },

    // set the style (default, stored) when switching back to the layout
    setCurrrentLayoutStyle() {
      if (this.isVideoScreenLayout && this.isLayoutSettable()) {
        this.rightWidth.style.width = this.newLayoutSize.rightWidth;
        this.leftWidth.style.width = this.newLayoutSize.leftWidth;
        document.querySelector('.left_contents').style.width = this.newLayoutSize.leftWidth;
        this.video.style.paddingTop = '56.25%';
      } else if (this.isLayoutSettable()) {
        this.rightWidth.style.width = this.newLayoutSize.rightWidth;
        this.leftWidth.style.width = this.newLayoutSize.leftWidth;
        const switchToLayout2 = parseFloat(this.newLayoutSize.leftWidth) >= this.switchingPercentage * 100;
        this.adjustedMaterialWidth = 0;
        this.switchBetweenLayout1And2(switchToLayout2);
      } else if (this.isDefaultLayout) {
        this.rightWidth.style.width = '50%';
        this.rightWidth.style.marginLeft = 'unset';
        this.leftWidth.style.width = '50%';
        this.video.style.marginBottom = '66px';
        this.retainPDFWidth();
        this.switchSliderLayout(1);
      } else if (this.isMaterialLayout) {
        this.rightWidth.style.width = this.htmlIsRevamp || this.isCmsPreview ? '50%' : '24.529%';
        this.rightWidth.style.marginLeft = this.htmlIsRevamp || this.isCmsPreview ? '50%' : '75.471%';
        this.leftWidth.style.width = this.htmlIsRevamp || this.isCmsPreview ? '50%' : '75.471%';
        this.retainPDFWidth();
        this.switchSliderLayout(2);
      } else if (this.isVideoScreenLayout) {
        this.rightWidth.style.width = '31.76%';
        this.rightWidth.style.marginLeft = '75.48%';
        document.querySelector('.left_contents').style.width = '75.48%';
        this.leftWidth.style.width = '75.48%';
        this.video.style.paddingTop = '56.25%';
      }
    },

    // retain the pdf size when the material is decreasing to make it readable
    retainPDFWidth() {
      const pdf = document.querySelector('.pdf');
      if (pdf) {
        const fixedPDFWidth = this.isMaterialLayout ? 0.75 : 0.5;
        this.initialPdfWidth = (window.innerWidth * fixedPDFWidth - 30) * (this.pdfZoom / 100) + 'px';

        if (this.$isMobileSize()) {
          pdf.style.width = '100%';
          this.setSliderPdfWidth(null);
        } else if (this.shouldUseFluidPDFWidth()) {
          // Not pinned by a slider drag: size relative to the panel (same as initial load)
          // instead of a window-based pixel width that can overflow the panel and clip the material.
          pdf.style.width = this.pdfZoom + '%';
          this.setSliderPdfWidth(null);
        } else {
          pdf.style.width = this.initialPdfWidth;
          this.setSliderPdfWidth(this.initialPdfWidth);
        }
      }
    },

    // get the percentage of pixel in the window to always adapt on window size change
    getPercentageWidth(value) {
      const padding = 48;
      return (value / (this.windowInnerWidth() - padding)) * 100;
    },

    // check if video height is less than or equal the 16:9 (width:height) ratio
    isVideoHeightInRatio() {
      const heightRatio = (this.video.offsetWidth + this.videoMarginBottom()) * (9 / 16);
      return this.video.offsetHeight <= heightRatio;
    },

    // reset adjusted size on window changes to mobile view
    resetAdjustedSize() {
      if (!this.isVideoHeightInRatio() || !this.newLayoutSize.videoHeight) {
        this.video.style.height = 'auto';
        this.video.style.paddingTop = '56.25%';
        this.video.style.maxHeight = 'unset';
      }
      if (this.newLayoutSize.leftWidth && this.isVideoScreenLayout) {
        let videoWidth = this.video.offsetWidth;
        if (this.menuChatHeight) {
          videoWidth = this.isMenuVisible ? videoWidth - videoWidth * 0.19 : videoWidth + videoWidth * 0.23;
        }
        this.chat.style.width = this.windowInnerWidth() - 72 - videoWidth + 'px';
      }
    },

    // reapply the adjusted size and add little timeout for rerendering of component
    reapplyVideoChatSize() {
      setTimeout(() => {
        this.video = document.querySelector('.video');
        this.reAdjustVideo();
      }, 50);
    },

    // set the stored video size upon switching back to layout with adjustment
    reAdjustVideo() {
      if (this.newLayoutSize.videoHeight) {
        this.video.style.height = this.newLayoutSize.videoHeight;
        if (!this.isVideoScreenLayout) {
          this.video.style.paddingTop = '0px';
        }
      }
      this.video.style.marginBottom = this.newLayoutSize.videoMarginBottom || this.videoMarginBottom() + 'px';
    },

    // adjust margin bottom on layout 2 where device selection resides before
    videoMarginBottom() {
      const hasLayout1 = parseFloat(this.newLayoutSize.leftWidth) < this.switchingPercentage * 100;
      return (this.isMaterialLayout && !hasLayout1) || this.toLayout2 ? 26 : 66;
    },

    // check if there's stored layout adjustment and layout is not `videoScreen`
    isLayoutSettable() {
      const storedLayoutAdjustment = this.newLayoutSize.rightWidth || this.newLayoutSize.leftWidth;
      return storedLayoutAdjustment;
    },

    // pdf follows the panel width on default/material layouts unless it was pinned by a slider drag
    shouldUseFluidPDFWidth() {
      return (this.isDefaultLayout || this.isMaterialLayout) && !this.isPdfWidthAdjusted;
    },

    // drop the slider-pinned pdf width so the material fits its panel again
    fitPDFToPanel() {
      this.isPdfWidthAdjusted = false;
      if (this.shouldUseFluidPDFWidth()) {
        // cleared even when .pdf is not rendered yet (material still loading)
        this.setSliderPdfWidth(null);
      }
      this.retainPDFWidth();
    },

    // set trainer tag dialog position on active word and prevent overflow on collapse
    setTrainerTagDialogPosition() {
      // get the x position of active word
      const activeWord = document.querySelector('.ql-editor .active');
      if (!activeWord) {
        return;
      }
      let activeWordClientX = activeWord.getBoundingClientRect().left;

      // set the left style from parent chatbox
      const leftSide = document.querySelector('.left_contents').offsetWidth + 90;
      let dialog = document.querySelector('.tags-content .el-dialog');
      dialog.style.left = activeWordClientX - leftSide + 'px';
      dialog.style.right = 'unset';

      let chatInput = document.querySelector('.quillWrapper').offsetHeight;
      dialog.style.bottom = chatInput + 4 + 'px';

      // check if the dialog overflow on the right side
      const maxPosition = window.innerWidth - 48 - dialog.offsetWidth;
      if (dialog.getBoundingClientRect().left > maxPosition) {
        dialog.style.right = '35px';
        dialog.style.left = 'unset';
      }
    }
  }
};
